/**
 * VoucherStorefrontPage.jsx — /vouchers — public catalog of vendor deals.
 *
 * Vendors publish voucher templates (e.g. "3-course dinner for $49
 * at any partner venue, retail $90"). Buyers browse the grid, click one,
 * and either buy it for themselves or send it as a gift.
 *
 * Reads:
 *   GET /api/vouchers/catalog                — list of active templates
 *   GET /api/vouchers/catalog/{template_id}  — one template
 * Writes:
 *   POST /api/vouchers/purchase              — Stripe Checkout
 *
 * Global chrome: PlatformHeader + SuiteFooter with i18n via useTranslation().
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, Ticket, MapPin, ArrowRight } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtMoney(cents = 0, cur = "AUD") {
  return `$${(cents / 100).toFixed(2)} ${cur}`;
}

/** One voucher template card. */
function TemplateCard({ tpl, onPick }) {
  const savings = Math.max(0, tpl.face_value_cents - tpl.purchase_price_cents);
  const pctOff = tpl.face_value_cents > 0
    ? Math.round(savings * 100 / tpl.face_value_cents) : 0;
  return (
    <button
      type="button"
      onClick={() => onPick(tpl)}
      data-testid={`tpl-${tpl.id}`}
      className="group text-left rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 hover:border-amber-400 transition"
    >
      <div
        className="h-40 bg-gradient-to-br from-amber-500 to-orange-600 relative"
        style={tpl.image_url ? {
          backgroundImage: `url(${tpl.image_url})`,
          backgroundSize: "cover", backgroundPosition: "center",
        } : undefined}
      >
        {pctOff > 0 && (
          <span className="absolute top-3 right-3 bg-amber-400 text-slate-950 text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
            {pctOff}% OFF
          </span>
        )}
        {tpl.is_gift_ready && (
          <span className="absolute top-3 left-3 bg-white/90 dark:bg-slate-950/80 text-amber-500 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1">
            <Gift size={11}/> Gift-ready
          </span>
        )}
      </div>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
          {tpl.vendor_name}
        </p>
        <h3 className="mt-1 font-black text-lg group-hover:text-amber-500 transition line-clamp-2">
          {tpl.item_description}
        </h3>
        <div className="mt-3 flex items-baseline gap-2">
          <p className="text-xl font-black text-amber-500">
            {fmtMoney(tpl.purchase_price_cents, tpl.currency)}
          </p>
          {savings > 0 && (
            <p className="text-xs text-stone-500 line-through">
              {fmtMoney(tpl.face_value_cents, tpl.currency)}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

/** Modal for the actual purchase flow. */
function PurchaseModal({ tpl, onClose }) {
  const [isGift,       setIsGift]        = useState(false);
  const [recipEmail,   setRecipEmail]    = useState("");
  const [recipName,    setRecipName]     = useState("");
  const [giftMsg,      setGiftMsg]       = useState("");
  const [busy,         setBusy]          = useState(false);
  const [error,        setError]         = useState("");

  if (!tpl) return null;

  const submit = async () => {
    setError("");
    if (isGift) {
      if (!recipEmail || !recipName) {
        setError("Recipient name + email required for gifts.");
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/vouchers/purchase`, {
        vendor_id:            tpl.vendor_id,
        vendor_name:          tpl.vendor_name,
        item_description:     tpl.item_description,
        kind:                 tpl.kind,
        purchase_price_cents: tpl.purchase_price_cents,
        face_value_cents:     tpl.face_value_cents,
        currency:             tpl.currency,
        quantity:             1,
        origin_url:           window.location.origin,
        is_gift:              isGift,
        gift_recipient_email: isGift ? recipEmail : undefined,
        gift_recipient_name:  isGift ? recipName  : undefined,
        gift_message:         isGift ? giftMsg    : undefined,
      }, { headers: authHeader(), withCredentials: true });
      if (data?.session_url) {
        window.location.href = data.session_url;
      } else {
        setError("Checkout unavailable");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.info("Please sign in to purchase a voucher.");
        window.location.href = `/login?next=/vouchers`;
        return;
      }
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="voucher-purchase-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
    >
      <div className="bg-white dark:bg-slate-950 text-stone-900 dark:text-stone-100 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 dark:border-stone-800">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
              {tpl.vendor_name}
            </p>
            <h2 className="font-black">{tpl.item_description}</h2>
          </div>
          <button onClick={onClose}
            data-testid="purchase-close"
            className="w-9 h-9 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center">✕</button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div className="text-center py-4">
            <p className="text-3xl font-black text-amber-500">
              {fmtMoney(tpl.purchase_price_cents, tpl.currency)}
            </p>
            {tpl.face_value_cents > tpl.purchase_price_cents && (
              <p className="text-sm text-stone-500 line-through">
                {fmtMoney(tpl.face_value_cents, tpl.currency)}
              </p>
            )}
          </div>
          {tpl.is_gift_ready && (
            <label className="flex items-center gap-2 text-sm cursor-pointer bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-xl px-4 py-3">
              <input
                type="checkbox"
                checked={isGift}
                onChange={(e) => setIsGift(e.target.checked)}
                data-testid="purchase-isgift"
              />
              <Gift size={14} className="text-amber-500"/>
              <span className="font-bold">Send this as a gift</span>
            </label>
          )}
          {isGift && (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Recipient's name"
                value={recipName}
                onChange={(e) => setRecipName(e.target.value)}
                data-testid="purchase-recip-name"
                className="w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
              />
              <input
                type="email"
                placeholder="Recipient's email"
                value={recipEmail}
                onChange={(e) => setRecipEmail(e.target.value)}
                data-testid="purchase-recip-email"
                className="w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
              />
              <textarea
                placeholder="Add a message (optional)"
                value={giftMsg}
                onChange={(e) => setGiftMsg(e.target.value)}
                rows={2}
                data-testid="purchase-gift-msg"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
              />
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-xs">
              {error}
            </div>
          )}
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="purchase-submit"
            className="w-full h-12 rounded-full bg-amber-400 text-slate-950 font-black hover:bg-amber-300"
          >
            {busy ? "Redirecting…" : `Buy ${isGift ? "as gift" : "for me"} — ${fmtMoney(tpl.purchase_price_cents, tpl.currency)}`}
          </Button>
          <p className="text-[10px] text-center text-stone-500">
            HustleSuite is the Merchant of Record. If unredeemed by expiry, 90% converts to credit at this vendor.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function VoucherStorefrontPage() {
  const [params]       = useSearchParams();
  const [templates,    setTemplates]    = useState(null);
  const [selectedTpl,  setSelectedTpl]  = useState(null);
  const [error,        setError]        = useState("");
  const { t, i18n } = useTranslation();
  const vendorFilter = params.get("vendor_id");

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "Vouchers & deals — HustleSuite";
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vouchers/catalog`, {
          params: vendorFilter ? { vendor_id: vendorFilter } : {},
        });
        setTemplates(data?.templates || []);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [vendorFilter, i18n.language]);

  const grouped = useMemo(() => {
    const byVendor = {};
    (templates || []).forEach((t) => {
      byVendor[t.vendor_id] = byVendor[t.vendor_id] || {
        vendor_id: t.vendor_id, vendor_name: t.vendor_name, tpls: [] };
      byVendor[t.vendor_id].tpls.push(t);
    });
    return Object.values(byVendor);
  }, [templates]);

  return (
    <div
      data-testid="voucher-storefront-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleSuite · Vouchers", to: "/vouchers" }}
        nav={[{ to: "/account/ledger",
                 label: t("nav_my_vouchers", { defaultValue: "My vouchers" }) }]}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <header className="mb-10">
          <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
            {t("vouchers_kicker", { defaultValue: "HustleSuite vouchers" })}
          </p>
          <h1 className="mt-2 text-3xl md:text-5xl font-black leading-tight">
            {t("vouchers_title", { defaultValue: "Deals worth booking. Gifts worth giving." })}
          </h1>
          <p className="mt-3 text-stone-500 dark:text-stone-400 max-w-2xl">
            {t("vouchers_sub", {
              defaultValue:
                "Every voucher lands in Apple Wallet + Google Wallet, works exactly like the vendor's own booking, and if it goes unredeemed you get 90% back as vendor credit — never zero.",
            })}
          </p>
        </header>

        {error && (
          <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-6"
            data-testid="vouchers-error">{error}</div>
        )}

        {templates === null && <p className="text-stone-400">Loading deals…</p>}
        {templates && templates.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-10 text-center"
            data-testid="vouchers-empty">
            <Ticket size={28} className="mx-auto text-amber-500"/>
            <p className="mt-4 font-semibold">
              {t("vouchers_empty_title", { defaultValue: "No deals yet — new vouchers land every week." })}
            </p>
          </div>
        )}
        {grouped.map((g) => (
          <section key={g.vendor_id} className="mb-10">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-xl font-black">{g.vendor_name}</h2>
              <span className="text-[11px] uppercase tracking-widest text-stone-500 inline-flex items-center gap-1">
                <MapPin size={11}/> {g.tpls.length} deal{g.tpls.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {g.tpls.map((t) => (
                <TemplateCard key={t.id} tpl={t} onPick={setSelectedTpl}/>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 flex items-center justify-between border-t border-stone-200 dark:border-stone-800 pt-6">
          <p className="text-xs text-stone-500">
            {t("vouchers_footnote", {
              defaultValue: "HustleSuite is the Merchant of Record. Every dollar traceable from buyer to vendor.",
            })}
          </p>
          <Link to="/account/ledger"
            className="text-sm font-bold text-amber-500 inline-flex items-center gap-1 hover:underline">
            {t("nav_my_vouchers", { defaultValue: "My vouchers" })} <ArrowRight size={14}/>
          </Link>
        </div>
      </div>

      {selectedTpl && (
        <PurchaseModal tpl={selectedTpl} onClose={() => setSelectedTpl(null)}/>
      )}
      <SuiteFooter />
    </div>
  );
}
